import React, { useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
  isHumanReviewDismissed,
  humanReviewDismissPayload,
} from '../../utils/checklistFormatting';

const HR_PAGE_SOFT_MAX = 5;
const HR_PAGE_HARD_MAX = 8;

/** Map page number -> { fileId, fileName } from a section's source_metadata. */
export function buildPageFileMap(sourceMetadata) {
  const map = new Map();
  const files = sourceMetadata?.files;
  if (!Array.isArray(files)) return map;
  const owners = new Map(); // page -> the one file listing it, or null when several do
  const ids = new Set();
  let def = null;
  for (const f of files) {
    if (!f || typeof f !== 'object') continue;
    const fileId = f.student_file_id ?? f.file_id ?? null;
    if (fileId == null) continue;
    ids.add(fileId);
    const entry = { fileId, fileName: f.file_name || null };
    if (!def) def = entry;
    const nums = [
      ...(Array.isArray(f.page_numbers) ? f.page_numbers : []),
      ...((Array.isArray(f.pages) ? f.pages : []).map((p) => p?.page)),
    ];
    for (const n of nums) {
      const pn = Number(n);
      if (!Number.isFinite(pn) || pn < 1) continue;
      const page = Math.trunc(pn);
      if (!owners.has(page)) owners.set(page, entry);
      else if (owners.get(page) && owners.get(page).fileId !== fileId) owners.set(page, null);
    }
  }
  owners.forEach((entry, page) => { if (entry) map.set(page, entry); });
  // One file: every page is in it. Several: each starts at page 1, so a bare page number
  // does not say which file - no default (it opened the wrong PDF for multi-file students).
  if (def && ids.size === 1) map.set('__default__', def);
  return map;
}

function normalizeFlagPages(pages) {
  if (!Array.isArray(pages)) return [];
  const best = new Map();
  for (const raw of pages) {
    const pageNum = Number(raw?.page ?? raw?.page_number ?? raw);
    if (!Number.isFinite(pageNum) || pageNum < 1) continue;
    const page = Math.trunc(pageNum);
    const score = Number(raw?.score);
    const row = {
      page,
      score: Number.isFinite(score) ? score : 0,
      student_file_id: raw?.student_file_id ?? raw?.file_id ?? null,
      file_name: raw?.file_name || raw?.source_file || null,
      ui_primary: raw?.ui_primary,
    };
    const prev = best.get(page);
    if (!prev || row.score > prev.score) best.set(page, row);
  }
  return Array.from(best.values())
    .sort((a, b) => b.score - a.score || a.page - b.page)
    .slice(0, HR_PAGE_HARD_MAX);
}

export function normalizeHumanReviewFlag(flag) {
  if (!flag) return null;
  if (typeof flag === 'string') {
    return {
      code: flag,
      message: flag.replace(/^\[[A-Z0-9_]+\]\s*/, ''),
      severity: 'warning',
      pages: [],
    };
  }
  return {
    code: flag.code || 'REVIEW',
    message: flag.message || flag.reason || flag.description
      || (flag.code ? String(flag.code).replace(/_/g, ' ') : 'Review suggested'),
    severity: flag.severity || 'warning',
    fields: flag.fields,
    suggestion: flag.suggestion || null,
    // A real gap the state does not require this LEA to correct — reviewers may waive it.
    dismissible: Boolean(flag.dismissible),
    pages: normalizeFlagPages(flag.pages),
  };
}

export function collectHumanReviewBySection(compliance, sectionKeys, { actionableOnly = false } = {}) {
  if (!compliance) return [];
  const keys = sectionKeys?.length ? sectionKeys : Object.keys(compliance);
  const groups = [];
  for (const key of keys) {
    const sec = compliance[key];
    const raw = sec?.human_review_flags;
    if (!Array.isArray(raw) || raw.length === 0) continue;
    const flags = raw
      .map(normalizeHumanReviewFlag)
      .filter(Boolean)
      .filter((f) => !actionableOnly || f.severity !== 'info')
      .filter((f) => !isHumanReviewDismissed(f, sec?.dismissed_findings));
    if (flags.length > 0) {
      groups.push({ sectionKey: key, flags, pageFileMap: buildPageFileMap(sec?.source_metadata) });
    }
  }
  return groups;
}

export function countHumanReviewFlags(compliance, sectionKeys) {
  return collectHumanReviewBySection(compliance, sectionKeys).reduce((n, g) => n + g.flags.length, 0);
}

export function DismissReviewButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      disabled={disabled}
      title="Remove this review item"
      aria-label="Remove this review item"
      className="p-1 rounded-md text-amber-800/70 hover:text-red-600 hover:bg-red-50 dark:text-amber-300/80 dark:hover:text-red-400 dark:hover:bg-red-900/20 flex-shrink-0 transition-colors disabled:opacity-40"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

const SEVERITY_STYLES = {
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

export function HumanReviewChip({ count, onClick, label, className = '' }) {
  const display = count > 99 ? '99+' : count;
  // Use span+role (not <button>) so this chip can sit inside SectionCard's <button>.
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      title={label || `${count} item${count !== 1 ? 's' : ''} need staff review`}
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 transition-colors whitespace-nowrap cursor-pointer ${className}`}
    >
      <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {label ? <span>{label}</span> : <span>{display}</span>}
    </span>
  );
}

export function FlagPageLinks({ pages, onOpenFileAtPage, pageFileMap }) {
  const [expanded, setExpanded] = useState(false);
  if (!pages?.length) return null;

  const primary = pages.filter((p) => p.ui_primary !== false).slice(0, HR_PAGE_SOFT_MAX);
  const shown = expanded ? pages : primary;
  const hiddenCount = Math.max(0, pages.length - shown.length);

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 mr-0.5">
        Source:
      </span>
      {shown.map((pg) => {
        const mapped = pageFileMap?.get(pg.page);
        const def = pageFileMap?.get('__default__');
        const fileId = pg.student_file_id ?? mapped?.fileId ?? def?.fileId ?? null;
        const fileName = pg.file_name || mapped?.fileName || def?.fileName || null;
        const canLink = fileId != null && onOpenFileAtPage;
        const label = `p. ${pg.page}`;
        if (canLink) {
          return (
            <button
              key={`${fileId}-${pg.page}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenFileAtPage(fileId, pg.page, fileName);
              }}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
              title={fileName ? `${fileName} — p. ${pg.page}` : label}
            >
              {label}
            </button>
          );
        }
        return (
          <span
            key={`p-${pg.page}`}
            className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-xs font-medium"
            title={fileName || undefined}
          >
            {label}
          </span>
        );
      })}
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:underline ml-0.5"
        >
          +{hiddenCount} more
        </button>
      )}
    </div>
  );
}

export default function HumanReviewModal({
  open,
  compliance,
  sectionConfig = {},
  sectionKey = null,
  sectionKeys = null,
  title,
  onClose,
  onOpenSection,
  onOpenFileAtPage,
  onDismissFlag,
}) {
  const [dismissingKey, setDismissingKey] = useState(null);

  const handleDismiss = async (key, flag, idx) => {
    const payload = humanReviewDismissPayload(flag);
    if (!payload || !onDismissFlag) return;
    const rowKey = `${key}-${flag.code || idx}`;
    setDismissingKey(rowKey);
    try {
      await onDismissFlag(key, payload);
    } finally {
      setDismissingKey(null);
    }
  };

  if (!open) return null;

  const allGroups = collectHumanReviewBySection(compliance, sectionKeys);
  const groups = sectionKey
    ? allGroups.filter((g) => g.sectionKey === sectionKey)
    : allGroups;
  const totalFlags = groups.reduce((n, g) => n + g.flags.length, 0);

  const modalTitle = title
    || (sectionKey && sectionConfig[sectionKey]?.label
      ? `Human review — ${sectionConfig[sectionKey].label}`
      : 'Human review suggested');

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={modalTitle}
      description={`${totalFlags} item${totalFlags !== 1 ? 's' : ''} flagged for staff verification`}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
        <div className="space-y-4">
          {groups.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-8">No review flags for this scope.</p>
          ) : (
            groups.map(({ sectionKey: key, flags, pageFileMap }) => (
              <section key={key} className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10 overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-100/60 dark:bg-amber-900/20 border-b border-amber-200/80 dark:border-amber-800/40">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 truncate">
                    {sectionConfig[key]?.label || key}
                  </h4>
                  {onOpenSection && (
                    <button
                      type="button"
                      onClick={() => onOpenSection(key)}
                      className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0"
                    >
                      Open section
                    </button>
                  )}
                </div>
                <ul className="divide-y divide-amber-100 dark:divide-amber-900/30">
                  {flags.map((flag, idx) => (
                    <li key={`${flag.code}-${idx}`} className="px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <span className={`mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 ${SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.warning}`}>
                          {flag.severity === 'info' ? 'Info' : 'Review'}
                        </span>
                        {flag.dismissible && (
                          <span
                            className="mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            title="The state does not require this LEA to correct this — may be waived."
                          >
                            Optional
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-800 dark:text-slate-200 leading-snug">{flag.message}</p>
                          {flag.suggestion && (
                            <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-1 leading-snug">
                              {flag.suggestion}
                            </p>
                          )}
                          {Array.isArray(flag.fields) && flag.fields.length > 0 && (
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                              Fields: {flag.fields.join(', ')}
                            </p>
                          )}
                          <FlagPageLinks pages={flag.pages} onOpenFileAtPage={onOpenFileAtPage} pageFileMap={pageFileMap} />
                          {flag.code && flag.code !== flag.message && (
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 font-mono">{flag.code}</p>
                          )}
                        </div>
                        {onDismissFlag && (
                          <DismissReviewButton
                            disabled={dismissingKey === `${key}-${flag.code || idx}`}
                            onClick={() => handleDismiss(key, flag, idx)}
                          />
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
    </Modal>
  );
}
